import { Link } from 'react-router-dom';
import { CTASection } from '../components/CTASection';
import { PageHero } from '../components/PageHero';
import { Section, SectionHeading, Reveal } from '../components/Section';
import { ServiceCard } from '../components/ServiceCard';
import { featuredServices, serviceDetails } from '../data/services';
import { Icon } from '../lib/icons';
import { SEO } from '../lib/seo';

const corePrinciples = [
  'Clear scope before build work starts',
  'Practical architecture matched to business risk',
  'Documentation that can be used by owners, staff, and future vendors',
  'Support paths for hosting, maintenance, improvements, and emergencies'
];

export function Services() {
  return (
    <>
      <SEO
        title="Services"
        description="Software development, automation, infrastructure, networking, AI systems, managed hosting, homelab services, and support from MaytoMation in Massachusetts."
      />
      <PageHero
        eyebrow="Services"
        title="Practical technology services for real business operations."
        body="MaytoMation works across software, automation, infrastructure, networking, AI systems, managed hosting, and long-term support. The common thread is maintainability: systems your team can understand, operate, and improve."
        primary={{ label: 'Book Consultation', href: '/booking' }}
        secondary={{ label: 'See Pricing', href: '/pricing' }}
        highlights={['Software development', 'Automation systems', 'Infrastructure support', 'Managed hosting']}
      />

      <Section>
        <SectionHeading
          eyebrow="What We Build"
          title="One engineering partner for connected business systems."
          body="Business technology problems rarely stay inside one category. A lead workflow may need software, hosting, CRM integration, email automation, reporting, and support. MaytoMation can own the whole technical path."
        />
        <div className="mt-12 grid gap-5 md:grid-cols-2 lg:grid-cols-3">
          {featuredServices.map((service) => (
            <Reveal key={service.title}>
              <ServiceCard {...service} />
            </Reveal>
          ))}
        </div>
      </Section>

      <Section className="bg-white">
        <div className="grid gap-10 lg:grid-cols-[.9fr_1.1fr] lg:items-start">
          <Reveal>
            <SectionHeading
              eyebrow="Service Areas"
              title="Focused pages for the work businesses ask for most."
              body="Each service area includes common problems, project examples, planning estimates, FAQs, and a consultation path."
            />
          </Reveal>
          <div className="grid gap-4">
            {serviceDetails.map((service) => (
              <Reveal key={service.slug}>
                <Link
                  to={`/services/${service.slug}`}
                  className="group grid gap-5 rounded-lg border border-brand-border bg-brand-background p-6 shadow-card transition hover:-translate-y-1 hover:bg-white hover:shadow-soft sm:grid-cols-[auto_1fr_auto] sm:items-center"
                >
                  <div className="flex h-12 w-12 items-center justify-center rounded-md bg-white text-brand-navy">
                    <Icon name={service.icon} className="h-6 w-6" />
                  </div>
                  <div>
                    <p className="text-xs font-extrabold uppercase tracking-[0.14em] text-brand-teal">
                      {service.eyebrow}
                    </p>
                    <h2 className="mt-2 text-xl font-extrabold text-brand-dark">{service.title}</h2>
                    <p className="mt-2 text-sm leading-7 text-brand-muted">{service.summary}</p>
                  </div>
                  <Icon name="arrowRight" className="h-5 w-5 text-brand-navy transition group-hover:translate-x-1" />
                </Link>
              </Reveal>
            ))}
          </div>
        </div>
      </Section>

      <Section>
        <SectionHeading
          eyebrow="How Engagements Work"
          title="The service is not just the build. It is the handoff, support model, and long-term clarity."
          body="MaytoMation engagements are designed for owners and operators who need dependable systems instead of one-off technical surprises."
        />
        <div className="mt-10 grid gap-4 md:grid-cols-2">
          {corePrinciples.map((principle) => (
            <Reveal key={principle}>
              <div className="flex gap-4 rounded-lg border border-brand-border bg-white p-5 shadow-card">
                <Icon name="check" className="mt-1 h-5 w-5 shrink-0 text-brand-teal" />
                <p className="text-base font-bold leading-7 text-brand-dark">{principle}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </Section>

      <CTASection title="Need a system that connects software, operations, and support?" button="Start the Conversation" />
    </>
  );
}
